import type { PosSalesLayoutDocument } from "../gateway/sales-layout-client";

export type SalesLayoutCatalogCategory = {
  categoryId: string;
  name: string;
};

type SalesLayoutNavigationOrderGroup = {
  categoryIds: string[];
  manualProductOrder: string[];
};

export type SalesLayoutNavigationItem = {
  key: string;
  kind: "ALL" | "CUSTOM_TAB" | "CATEGORY";
  label: string;
  categoryIds: string[];
  manualProductOrder: string[];
  orderingGroups?: SalesLayoutNavigationOrderGroup[];
};

const ALL_NAVIGATION_ITEM: SalesLayoutNavigationItem = {
  key: "ALL",
  kind: "ALL",
  label: "ทั้งหมด",
  categoryIds: [],
  manualProductOrder: [],
};

function uniqueValues(values: Array<string | null | undefined>) {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const normalized = value?.trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
  }
  return result;
}

export function resolveSalesLayoutNavigation(
  layout: PosSalesLayoutDocument | null,
  catalogCategories: SalesLayoutCatalogCategory[],
): SalesLayoutNavigationItem[] {
  const categoryById = new Map(
    catalogCategories
      .filter((category) => category.categoryId.trim() && category.name.trim())
      .map((category) => [category.categoryId.trim(), {
        categoryId: category.categoryId.trim(),
        name: category.name.trim(),
      }] as const),
  );

  const customTabs: SalesLayoutNavigationItem[] = [];
  const assignedCategoryIds = new Set<string>();

  for (const tab of layout?.tabs ?? []) {
    const tabId = tab.tab_id?.trim();
    const label = tab.name?.trim();
    if (!tabId || !label) continue;

    const configuredCategoryIds = uniqueValues(
      (tab.sections ?? [])
        .filter((section) => section.source_type === "CATEGORY")
        .map((section) => section.category_id),
    );
    const eligibleCategoryIds = configuredCategoryIds.filter((categoryId) => categoryById.has(categoryId));
    if (!eligibleCategoryIds.length) continue;

    eligibleCategoryIds.forEach((categoryId) => assignedCategoryIds.add(categoryId));
    customTabs.push({
      key: `TAB:${tabId}`,
      kind: "CUSTOM_TAB",
      label,
      categoryIds: eligibleCategoryIds,
      manualProductOrder: tab.presentation_mode === "MIXED"
        ? uniqueValues(tab.manual_product_order ?? [])
        : [],
    });
  }

  const fallbackCategories = Array.from(categoryById.values())
    .filter((category) => !assignedCategoryIds.has(category.categoryId))
    .sort((left, right) => left.name.localeCompare(right.name, "th"))
    .map<SalesLayoutNavigationItem>((category) => ({
      key: `CATEGORY:${category.categoryId}`,
      kind: "CATEGORY",
      label: category.name,
      categoryIds: [category.categoryId],
      manualProductOrder: [],
    }));

  const orderedNavigation = [...customTabs, ...fallbackCategories];
  const allNavigation: SalesLayoutNavigationItem = {
    ...ALL_NAVIGATION_ITEM,
    orderingGroups: orderedNavigation.map((item) => ({
      categoryIds: item.categoryIds,
      manualProductOrder: item.manualProductOrder,
    })),
  };

  return [allNavigation, ...orderedNavigation];
}

export function salesLayoutNavigationMatchesCategory(
  navigation: SalesLayoutNavigationItem,
  categoryId: string | null | undefined,
) {
  if (navigation.kind === "ALL") return true;
  if (!categoryId) return false;
  return navigation.categoryIds.includes(categoryId);
}

type SalesLayoutOrderableEntry = {
  menuItemId: string;
  snapshot?: {
    menu?: {
      menu_category_id?: string | null;
    };
  };
};

function entryCategoryId(entry: SalesLayoutOrderableEntry) {
  return entry.snapshot?.menu?.menu_category_id?.trim() || null;
}

function appendManualProductOrder<T extends SalesLayoutOrderableEntry>(
  ordered: T[],
  seen: Set<string>,
  entries: T[],
  manualProductOrder: string[],
) {
  if (!manualProductOrder.length) return;
  const entryById = new Map(entries.map((entry) => [entry.menuItemId, entry] as const));
  for (const productId of manualProductOrder) {
    const entry = entryById.get(productId);
    if (!entry || seen.has(entry.menuItemId)) continue;
    ordered.push(entry);
    seen.add(entry.menuItemId);
  }
}

export function resolveSalesLayoutMenuOrder<T extends SalesLayoutOrderableEntry>(
  navigation: SalesLayoutNavigationItem,
  entries: T[],
): T[] {
  if (navigation.kind === "ALL" && navigation.orderingGroups?.length) {
    const ordered: T[] = [];
    const seen = new Set<string>();

    for (const group of navigation.orderingGroups) {
      const categoryIds = new Set(group.categoryIds);
      const groupEntries = entries.filter((entry) => {
        const categoryId = entryCategoryId(entry);
        return Boolean(categoryId && categoryIds.has(categoryId));
      });

      appendManualProductOrder(ordered, seen, groupEntries, group.manualProductOrder);

      // The ALL view follows the same semantic order as the Sales Layout tabs/sections.
      // AUTO_INCLUDE products keep their deterministic Catalog order inside each category.
      for (const categoryId of group.categoryIds) {
        for (const entry of groupEntries) {
          if (seen.has(entry.menuItemId) || entryCategoryId(entry) !== categoryId) continue;
          ordered.push(entry);
          seen.add(entry.menuItemId);
        }
      }
    }

    // Preserve any unexpected/unassigned runtime entries rather than hiding sellable products.
    for (const entry of entries) {
      if (seen.has(entry.menuItemId)) continue;
      ordered.push(entry);
      seen.add(entry.menuItemId);
    }

    return ordered;
  }

  if (navigation.kind !== "CUSTOM_TAB" || navigation.manualProductOrder.length === 0) {
    return entries;
  }

  const ordered: T[] = [];
  const seen = new Set<string>();
  appendManualProductOrder(ordered, seen, entries, navigation.manualProductOrder);

  // AUTO_INCLUDE remainder stays deterministic by retaining the Catalog Local Runtime order.
  // This is the generic propagation path for products published after the manual list was saved.
  for (const entry of entries) {
    if (seen.has(entry.menuItemId)) continue;
    ordered.push(entry);
    seen.add(entry.menuItemId);
  }

  return ordered;
}
